import pluralize from 'pluralize'
import {
  ClipboardEvent,
  FC,
  KeyboardEvent,
  PropsWithChildren,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FieldError } from 'react-hook-form'

import { cn } from '../utils'
import { Button } from './button'
import { Icon } from './icon'
import { inputStyles } from './input'

interface MultiValueProps {
  value: string[]
  error?: FieldError
  onChange: (v: string[]) => void
  onBlur: () => void
  keyTriggers?: string[]
  placeholder?: string
  label?: string
  validator?: (v: string[]) => string[] //returns invalid values
}

interface PillProps extends PropsWithChildren {
  error?: boolean
  onRemove?: () => void
}

function Pill(props: PillProps) {
  return (
    <div
      className={cn(
        'bg-input-background text-foreground flex items-center gap-1 whitespace-normal rounded-sm px-1',
        {
          'text-destructive ring-destructive ring-2': props.error,
        },
      )}
    >
      {props.children}
      <Button
        size="icon-sm"
        variant="ghost"
        className="mt-1 h-4 w-4 rounded-md"
        onClick={props.onRemove}
      >
        <Icon name="x" size={10} />
      </Button>
    </div>
  )
}

const MultiValue: FC<MultiValueProps> = ({
  keyTriggers = ['Enter', ',', ';', ' '],
  ...props
}) => {
  const value = Array.isArray(props.value) ? props.value : []
  const ref = useRef<HTMLInputElement>(null)
  const [showPlaceholder, setShowPlaceholder] = useState<boolean>(
    props.value.length === 0,
  )
  const invalidValues = useMemo(
    () => (props.validator ? props.validator(props.value) : []),
    [props],
  )

  const handleNewValue = (newValue: string | string[] | undefined) => {
    if (!ref.current)
      throw new Error('MultiValue: missing hidden input to reference value from.')
    if (!newValue) return
    const arrayValue = typeof newValue === 'string' ? [newValue] : newValue
    props.onChange([
      ...value,
      ...arrayValue.filter((v) => v && !value.includes(v)).map((v) => v.toLowerCase()),
    ])
    ref.current.innerText = ''
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (keyTriggers.includes(e.key)) {
      e.preventDefault()
      handleNewValue(ref.current?.innerText)
    }

    if (ref.current?.innerText === '') {
      setShowPlaceholder(false)
    }

    if (e.key === 'Backspace' && !ref.current?.innerText) {
      props.onChange(props.value.slice(0, -1))
      if (ref.current?.innerText.length === 1) {
        setShowPlaceholder(true)
      }
    }
  }

  const handleBlur = () => {
    if (value.length === 0) setShowPlaceholder(true)
    handleNewValue(ref.current?.innerText)
    props.onBlur()
  }

  const handlePaste = (e: ClipboardEvent) => {
    e.preventDefault()
    const value = e.clipboardData.getData('Text').replace(/\n|\r/g, '')
    handleNewValue(value.split(new RegExp(keyTriggers.join('|'))))
  }

  const handleRemove = (index: number) => {
    const nextValue = [...value]
    nextValue.splice(index, 1)
    props.onChange(nextValue)
  }

  return (
    <div className="relative flex flex-col">
      <div
        className={cn(
          inputStyles,
          'focus-within:border-input-focus-border focus-within:bg-input-background flex h-auto min-h-12 cursor-text flex-wrap items-center gap-2 py-2 focus-within:border focus-within:shadow-[0px_0px_0px_3px_hsl(var(--input-focus-shadow))]',
        )}
        onClick={() => ref.current?.focus()}
      >
        {value.map((v, i) => (
          <Pill
            key={i}
            onRemove={() => handleRemove(i)}
            error={invalidValues.includes(v)}
          >
            {v}
          </Pill>
        ))}
        <span
          className="text-foreground outline-hidden flex items-center border-none"
          ref={ref}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
          contentEditable
        />
        {showPlaceholder && (
          <span className="nowrap user-select-none text-input-secondary-foreground">
            {props.placeholder}
          </span>
        )}
      </div>
      <span className="mt-sm">
        {invalidValues.length ? (
          <>
            <span className="text-warn text-sm font-medium">
              {pluralize('Error', invalidValues.length, true)}:{' '}
            </span>
            <span
              className="cursor-pointer text-sm font-medium hover:underline"
              onClick={() => {
                ref.current?.focus()
                props.onChange(value.filter((v) => !invalidValues.includes(v)))
              }}
            >
              Remove all items with errors
            </span>
          </>
        ) : null}
      </span>
    </div>
  )
}

export default MultiValue
